/**
 * Drag-to-look inversion shared by every camera mode.
 *
 * The scene setting `invertCameraRotation` (editor: "Invert Drag Direction") reverses BOTH drag
 * axes — the Babylon-era `camera.invertRotation` semantics, "natural panning as opposed to direct
 * panning". Fly, Walk and Tour look-around default to first-person (the camera turns the way you
 * drag); with the toggle on, the scene follows the pointer instead (Google Maps feel). Orbit already
 * follows the pointer by default, so the toggle reverses it there.
 *
 * Every pointer/touch drag consumer (CameraControls orbit + fly, CharacterController walk, the
 * tour elastic look-around) must route its delta through here so the toggle means one thing.
 * Gamepad sticks are NOT drags and keep the conventional invert-Y meaning in their own code.
 */

/** Sign multiplier for a drag-look axis: -1 when inverted, +1 otherwise. */
export function lookAxisSign(invert: boolean | undefined): 1 | -1 {
    return invert ? -1 : 1;
}

/** Apply the inversion to a single drag-look axis delta. */
export function invertLookAxis(delta: number, invert: boolean | undefined): number {
    return invert ? -delta : delta;
}

/**
 * Apply the inversion in place to a 2D drag delta (x = horizontal / yaw, y = vertical / pitch).
 * Mutates and returns `delta` so hot paths can reuse their scratch vectors without allocating.
 */
export function applyLookInversion<T extends { x: number; y: number }>(delta: T, invert: boolean | undefined): T {
    if (invert) {
        delta.x = -delta.x;
        delta.y = -delta.y;
    }
    return delta;
}
